import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmt, fmtQty, fmtDate } from '../../utils/api';
import { aggregateByModel } from '../../utils/modelKey';
import * as XLSX from 'xlsx';

export default function FCOrdersPage() {
  const { t, i18n } = useTranslation();
  const { hasRole } = useAuth();
  const he = i18n.language === 'he';
  const canEdit = hasRole('admin', 'warehouse');
  const [soOrders, setSoOrders] = useState([]);
  const [fcOrders, setFcOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [reviseEdits, setReviseEdits] = useState({}); // { itemId: qty }
  const [revising, setRevising] = useState(null); // fc id being revised
  const [selectedFcs, setSelectedFcs] = useState(new Set()); // multi-select for per-model totals
  const [allFcItems, setAllFcItems] = useState({}); // { fcId: items[] } cache for aggregation

  // Preview state
  const [preview, setPreview] = useState(null); // { soOrderNo, items, warnings, safe }
  const [previewMonth, setPreviewMonth] = useState('');
  const [previewETA, setPreviewETA] = useState('');
  const [previewChecked, setPreviewChecked] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [soRes, fcRes] = await Promise.all([
        api.get('/logistics').then(r => (r.data.orders || r.data || []).filter(o => o.doc_type === 'SO')),
        api.get('/portal/fc-list'),
      ]);
      setSoOrders(soRes);
      setFcOrders(fcRes.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = (key) => setExpanded(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  const sentSOs = new Set(fcOrders.map(f => f.so_order_no));

  // Multi-select + per-model totals
  const ensureItemsLoaded = useCallback(async (fcIds) => {
    const missing = [...fcIds].filter(id => !allFcItems[id]);
    if (missing.length === 0) return;
    try {
      const res = await api.get('/portal/fc-orders');
      const map = { ...allFcItems };
      for (const fc of res.data || []) if (missing.includes(fc.id)) map[fc.id] = fc.items || [];
      setAllFcItems(map);
    } catch {}
  }, [allFcItems]);

  const toggleSelectFc = async (fcId) => {
    const next = new Set(selectedFcs);
    if (next.has(fcId)) next.delete(fcId); else next.add(fcId);
    setSelectedFcs(next);
    if (next.size > 0) await ensureItemsLoaded(next);
  };

  const selectedItems = [...selectedFcs].flatMap(id => allFcItems[id] || []);
  const modelSummary = aggregateByModel(selectedItems, 'effectiveQty');

  const openPreview = async (soOrderNo) => {
    try {
      const res = await api.get(`/portal/fc-preview/${soOrderNo}`);
      setPreview(res.data);
      setPreviewMonth('');
      setPreviewETA('');
      setPreviewChecked(false);
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };

  const confirmSend = async () => {
    if (!preview || !previewChecked) return;
    setSending(true);
    try {
      await api.post('/portal/fc-send', { soOrderNo: preview.soOrderNo, month: previewMonth, estimatedArrival: previewETA || null });
      setPreview(null);
      await fetchData();
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
    finally { setSending(false); }
  };

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>{he ? 'הזמנות FC' : 'FC ORDERS'}</h2>

      {/* ═══ Preview Modal ═══ */}
      {preview && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={() => setPreview(null)}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 900, width: '95%', maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background: '#1565c0', color: '#fff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{he ? 'תצוגה מקדימה — מה הלקוח יראה' : 'PREVIEW — What Customer Will See'}</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>{preview.soOrderNo}</div>
              </div>
              <button onClick={() => setPreview(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ padding: 20, maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Warnings */}
              {preview.warnings.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>⚠️ {he ? 'אזהרות' : 'Warnings'}</div>
                  {preview.warnings.map((w, i) => (
                    <div key={i} style={{ fontSize: 13, color: '#991b1b' }}>• {w}</div>
                  ))}
                </div>
              )}

              {/* Items table — exactly what customer sees */}
              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table>
                  <thead><tr>
                    <th>{he ? 'מק"ט' : 'SKU'}</th>
                    <th>{he ? 'מוצר' : 'Product'}</th>
                    <th className="text-end">{he ? 'כמות' : 'Qty'}</th>
                    <th className="text-end">{he ? 'מחיר ללקוח' : 'Customer Price'}</th>
                    <th className="text-end">{he ? 'סה"כ' : 'Total'}</th>
                  </tr></thead>
                  <tbody>
                    {preview.items.map((item, i) => (
                      <tr key={i} style={!item.customerPrice ? { background: '#fef2f2' } : {}}>
                        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{item.sku}{item.barcode && item.barcode !== item.sku && <div className="text-muted" style={{ fontSize: 10 }}>{item.barcode}</div>}</td>
                        <td>{item.description}</td>
                        <td className="text-end fw-bold">{fmtQty(item.qty)}</td>
                        <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", color: !item.customerPrice ? '#dc2626' : '#333' }}>
                          {item.customerPrice > 0 ? fmt(item.customerPrice) : <span style={{ color: '#dc2626', fontWeight: 700 }}>{he ? 'אין מחיר' : 'NO PRICE'}</span>}
                        </td>
                        <td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace" }}>{fmt(item.qty * item.customerPrice)}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 700, borderTop: '2px solid #dee2e6' }}>
                      <td colSpan={2}>{t('common.total')}</td>
                      <td className="text-end">{fmtQty(preview.items.reduce((s, i) => s + i.qty, 0))}</td>
                      <td></td>
                      <td className="text-end">{fmt(preview.items.reduce((s, i) => s + i.qty * i.customerPrice, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Month + ETA */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>{he ? 'חודש:' : 'Month:'}</label>
                <input className="input input-sm" placeholder={he ? 'לדוגמה: אפריל 2026' : 'e.g. April 2026'} value={previewMonth} onChange={e => setPreviewMonth(e.target.value)} style={{ width: 150 }} />
                <label style={{ fontSize: 13, fontWeight: 600 }}>ETA:</label>
                <input className="input input-sm" type="date" value={previewETA} onChange={e => setPreviewETA(e.target.value)} style={{ width: 160 }} />
              </div>

              {/* Safety check */}
              <div style={{ background: preview.safe ? '#f0fdf4' : '#fef2f2', border: `1px solid ${preview.safe ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
                {preview.safe ? (
                  <div style={{ color: '#166534', fontWeight: 600 }}>
                    ✅ {he ? 'בדיקה עברה — כל המחירים הם מחירי לקוח' : 'Check passed — all prices are customer prices from price list, not purchase prices'}
                  </div>
                ) : (
                  <div style={{ color: '#991b1b', fontWeight: 600 }}>
                    ❌ {he ? 'נמצאו בעיות — בדוק אזהרות למעלה' : 'Issues found — check warnings above'}
                  </div>
                )}
              </div>

              {/* Confirm checkbox */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
                <input type="checkbox" checked={previewChecked} onChange={e => setPreviewChecked(e.target.checked)} style={{ width: 20, height: 20 }} />
                {he ? 'אני מאשר שהמחירים נכונים' : 'I confirm these are the correct customer prices'}
              </label>

              {/* Action buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn btn-outline" onClick={() => setPreview(null)}>{he ? 'ביטול' : 'Cancel'}</button>
                <button className="btn" disabled={!previewChecked || sending}
                  style={{ background: previewChecked ? '#16a34a' : '#d1d5db', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: previewChecked ? 'pointer' : 'not-allowed' }}
                  onClick={confirmSend}>
                  {sending ? '...' : (he ? '✅ אשר ושלח ללקוח' : '✅ Confirm & Send to Customer')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SO Orders ═══ */}
      <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', marginBottom: 8 }}>
        {he ? 'הזמנות SO — לשליחה ללקוח' : 'SO Orders — Send to Customer'}
      </h3>
      {soOrders.length === 0 ? <div className="empty-state">{t('common.noData')}</div> :
        soOrders.map(o => {
          const isOpen = expanded.has(`so-${o.order_no}`);
          const alreadySent = sentSOs.has(o.order_no);
          const fc = fcOrders.find(f => f.so_order_no === o.order_no);
          return (
            <div key={o.order_no} className="order-group" style={{ marginBottom: 8 }}>
              <div className="order-group-header" onClick={() => toggle(`so-${o.order_no}`)} style={{ cursor: 'pointer' }}>
                <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                <span className="order-no">{o.order_no}</span>
                <span className="text-muted" style={{ fontSize: 12 }}>{fmtDate(o.order_date)}</span>
                <span className="text-muted" style={{ fontSize: 12 }}>{(o.items || []).length} {he ? 'מוצרים' : 'SKUs'} · {fmtQty(o.total_units)} {he ? 'יחידות' : 'units'}</span>
                <span className="fw-bold">{fmt(o.total_amount)}</span>
                <div style={{ flex: 1 }} />
                {alreadySent ? (
                  <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                    background: fc?.status === 'approved' ? '#dcfce7' : '#dbeafe',
                    color: fc?.status === 'approved' ? '#166534' : '#1e40af' }}>
                    {fc?.status === 'approved' ? `✅ ${he ? 'אושר' : 'Approved'}` : `📤 ${he ? 'נשלח' : 'Sent'}`}
                    {fc?.modified_count > 0 && ` · ${fc.modified_count} ${he ? 'שינויים' : 'changes'}`}
                  </span>
                ) : canEdit && (
                  <button className="btn btn-gold btn-sm"
                    onClick={(e) => { e.stopPropagation(); openPreview(o.order_no); }}>
                    {he ? '👁 תצוגה מקדימה' : '👁 Preview & Send'}
                  </button>
                )}
              </div>
              {isOpen && o.items && (
                <div className="order-group-body">
                  <div className="table-wrap">
                    <table>
                      <thead><tr>
                        <th>{t('common.sku')}</th>
                        <th>{t('common.product')}</th>
                        <th className="text-end">{t('warehouse.qty')}</th>
                        <th className="text-end">{he ? 'מחיר SO' : 'SO Price'}</th>
                        <th className="text-end">{t('common.total')}</th>
                      </tr></thead>
                      <tbody>
                        {o.items.map((item, i) => (
                          <tr key={i}>
                            <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{item.sku}</td>
                            <td>{item.description}</td>
                            <td className="text-end fw-bold">{fmtQty(item.quantity)}</td>
                            <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{item.unitPrice ? fmt(item.unitPrice) : '—'}</td>
                            <td className="text-end fw-bold">{fmt(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })
      }

      {/* ═══ Sticky per-model totals ═══ */}
      {selectedFcs.size > 0 && modelSummary.length > 0 && (
        <div style={{
          position: 'sticky', top: 70, zIndex: 50,
          background: 'linear-gradient(135deg,#dbeafe,#bfdbfe)',
          border: '2px solid #1d4ed8', borderRadius: 8,
          padding: 12, marginTop: 16,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>
              📊 {he ? `סיכום ${selectedFcs.size} הזמנות לפי מודל` : `Summary of ${selectedFcs.size} orders by model`}
            </strong>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: '#1e3a8a' }}>
              {modelSummary.reduce((s, m) => s + m.totalQty, 0).toLocaleString()} {he ? 'יח׳ סה"כ' : 'units total'} ·
              {' '}{fmt(modelSummary.reduce((s, m) => s + m.totalValue, 0))}
            </span>
            <button className="btn btn-sm btn-outline" onClick={() => setSelectedFcs(new Set())}
              style={{ fontSize: 11 }}>{he ? 'נקה' : 'Clear'}</button>
            <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
              onClick={() => {
                const rows = [];
                for (const m of modelSummary) {
                  rows.push({ Model: m.model, Color: '', 'Qty': m.totalQty, 'Value': m.totalValue, Lines: m.lineCount });
                  for (const c of m.colors) rows.push({ Model: '', Color: c.color, 'Qty': c.qty, 'Value': c.value, Lines: c.lineCount });
                  rows.push({});
                }
                const ws = XLSX.utils.json_to_sheet(rows);
                ws['!cols'] = [{ wch: 22 }, { wch: 22 }, { wch: 10 }, { wch: 14 }, { wch: 8 }];
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'By Model + Color');
                XLSX.writeFile(wb, `FC_summary_by_model_${selectedFcs.size}orders.xlsx`);
              }}>📥 Excel</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            {modelSummary.map(m => (
              <div key={m.model} style={{ background: '#fff', padding: '8px 12px', borderRadius: 6, border: '1px solid #93c5fd' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{m.model}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#1d4ed8' }}>
                  {m.totalQty.toLocaleString()} <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>{he ? 'יח׳' : 'units'}</span>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{m.lineCount} {he ? 'שורות' : 'lines'} · {fmt(m.totalValue)}</div>
                {m.colors.length > 0 && (
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {m.colors.map(c => (
                      <div key={c.color} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, alignItems: 'baseline' }}>
                        <span style={{ color: '#475569' }}>{c.color}</span>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.qty.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ Sent FC Orders ═══ */}
      {fcOrders.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', marginBottom: 8 }}>
            {he ? 'הזמנות שנשלחו — תגובת לקוח' : 'Sent Orders — Customer Response'}
          </h3>
          {fcOrders.map(fc => {
            const isOpen = expanded.has(`fc-${fc.id}`);
            const loadItems = async () => {
              if (fc._items) { toggle(`fc-${fc.id}`); return; }
              try {
                const res = await api.get('/portal/fc-orders');
                const match = (res.data || []).find(f => f.id === fc.id);
                if (match) { setFcOrders(prev => prev.map(f => f.id === fc.id ? { ...f, _items: match.items } : f)); }
              } catch {}
              toggle(`fc-${fc.id}`);
            };

            const isSelected = selectedFcs.has(fc.id);
            return (
              <div key={fc.id} className="order-group" style={{ marginBottom: 8, outline: isSelected ? '2px solid var(--gold)' : 'none', borderRadius: 6 }}>
                <div className="order-group-header" onClick={loadItems} style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={isSelected}
                    onClick={e => { e.stopPropagation(); }}
                    onChange={() => toggleSelectFc(fc.id)}
                    style={{ marginInlineEnd: 8, width: 16, height: 16, cursor: 'pointer' }} />
                  <span className={`arrow${isOpen ? ' open' : ''}`}>&#9654;</span>
                  <span className="order-no">{fc.so_order_no}</span>
                  {fc.month && <span className="text-muted" style={{ fontSize: 12 }}>{fc.month}</span>}
                  <span className="text-muted" style={{ fontSize: 12 }}>{he ? 'נשלח' : 'Sent'}: {fmtDate(fc.sent_at)}</span>
                  <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                    background: fc.status === 'approved' ? '#dcfce7' : '#fef3c7',
                    color: fc.status === 'approved' ? '#166534' : '#92400e' }}>
                    {fc.status === 'approved' ? `✅ ${he ? 'אושר' : 'Approved'}` : `⏳ ${he ? 'ממתין' : 'Pending'}`}
                  </span>
                  {parseInt(fc.modified_count) > 0 && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#fee2e2', color: '#991b1b' }}>{fc.modified_count} {he ? 'שינויים' : 'changes'}</span>}
                  <div style={{ flex: 1 }} />
                  <span className="text-muted">{fc.item_count} {he ? 'מוצרים' : 'items'} · {fmtQty(parseFloat(fc.total_qty))} {he ? 'יחידות' : 'units'}</span>
                  <button className="btn btn-sm btn-outline" style={{ marginInlineStart: 8 }} onClick={async (e) => {
                    e.stopPropagation();
                    // Load items if needed
                    let items = fc._items;
                    if (!items) {
                      try { const res = await api.get('/portal/fc-orders'); const match = (res.data || []).find(f => f.id === fc.id); items = match?.items || []; } catch { return; }
                    }
                    const rows = items.map(item => {
                      const custQty = item.customerQty != null ? item.customerQty : item.originalQty;
                      return {
                        'SKU': item.sku, 'Barcode': item.barcode || '', 'Product': item.description,
                        'Original Qty': item.originalQty, 'Customer Qty': custQty,
                        'Changed': item.customerModified ? 'Yes' : '',
                        'Price': item.customerPrice, 'Total': custQty * item.customerPrice,
                      };
                    });
                    rows.push({});
                    rows.push({ 'SKU': '', 'Product': 'TOTAL', 'Original Qty': items.reduce((s, i) => s + i.originalQty, 0), 'Customer Qty': items.reduce((s, i) => s + (i.customerQty != null ? i.customerQty : i.originalQty), 0), 'Price': '', 'Total': items.reduce((s, i) => s + (i.customerQty != null ? i.customerQty : i.originalQty) * i.customerPrice, 0) });
                    const ws = XLSX.utils.json_to_sheet(rows);
                    ws['!cols'] = [{wch:10},{wch:16},{wch:35},{wch:12},{wch:12},{wch:8},{wch:10},{wch:14}];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, `FC ${fc.so_order_no}`);
                    XLSX.writeFile(wb, `FC_${fc.so_order_no}_${fc.status}.xlsx`);
                  }}>📥 Excel</button>
                  <button
                    className="btn btn-sm btn-outline"
                    style={{ marginInlineStart: 8, color: '#dc2626', borderColor: '#dc2626' }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const msg = he
                        ? `למחוק FC עבור ${fc.so_order_no}? פעולה זו תסיר את ההזמנה גם אצל הלקוח.`
                        : `Delete FC for ${fc.so_order_no}? This will also remove it from the customer's view.`;
                      if (!window.confirm(msg)) return;
                      try {
                        await api.delete(`/portal/fc-orders/${fc.id}`);
                        setFcOrders(prev => prev.filter(f => f.id !== fc.id));
                      } catch (err) {
                        alert((he ? 'שגיאה: ' : 'Error: ') + (err.response?.data?.error || err.message));
                      }
                    }}
                  >🗑 {he ? 'מחק' : 'Delete'}</button>
                </div>
                {isOpen && fc._items && (
                  <div className="order-group-body" style={{ padding: 16 }}>
                    <div className="table-wrap">
                      <table>
                        <thead><tr>
                          <th>{he ? 'מק"ט' : 'SKU'}</th>
                          <th>{he ? 'מוצר' : 'Product'}</th>
                          <th className="text-end">{he ? 'כמות מקורית' : 'Original Qty'}</th>
                          <th className="text-end" style={{ background: '#fffbeb' }}>{he ? 'כמות לקוח' : 'Customer Qty'}</th>
                          {revising === fc.id && <th className="text-end" style={{ background: '#e0f2fe' }}>{he ? 'כמות מעודכנת' : 'Revised Qty'}</th>}
                          <th className="text-end">{he ? 'מחיר' : 'Price'}</th>
                          <th className="text-end">{he ? 'סה"כ' : 'Total'}</th>
                        </tr></thead>
                        <tbody>
                          {fc._items.map(item => {
                            const custQty = item.customerQty != null ? item.customerQty : item.originalQty;
                            const changed = item.customerModified;
                            const revQty = reviseEdits[item.id] !== undefined ? reviseEdits[item.id] : custQty;
                            return (
                              <tr key={item.id} style={changed ? { background: '#fffbeb' } : {}}>
                                <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{item.sku}</td>
                                <td>{item.description}</td>
                                <td className="text-end">{fmtQty(item.originalQty)}</td>
                                <td className="text-end fw-bold" style={{ background: '#fffbeb', color: changed ? '#e65100' : '#333' }}>
                                  {fmtQty(custQty)}
                                  {changed && <span style={{ fontSize: 10, color: '#6b7280' }}> ({custQty > item.originalQty ? '+' : ''}{custQty - item.originalQty})</span>}
                                </td>
                                {revising === fc.id && (
                                  <td className="text-end" style={{ background: '#e0f2fe' }}>
                                    <input className="inline-edit" type="number" min="0"
                                      defaultValue={custQty} key={`rev-${item.id}`}
                                      onBlur={e => setReviseEdits(prev => ({ ...prev, [item.id]: parseInt(e.target.value) || 0 }))}
                                      onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                                      style={{ width: 80, textAlign: 'right', fontWeight: 700 }} />
                                  </td>
                                )}
                                <td className="text-end" style={{ fontFamily: "'DM Mono', monospace" }}>{fmt(item.customerPrice)}</td>
                                <td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace" }}>{fmt((revising === fc.id ? revQty : custQty) * item.customerPrice)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
                      {fc.status === 'approved' && (
                        <div style={{ flex: 1, padding: 8, background: '#dcfce7', borderRadius: 8, fontWeight: 600, color: '#166534', fontSize: 13 }}>
                          ✅ {he ? 'הלקוח אישר' : 'Customer Approved'} — {fmtDate(fc.customer_approved_at)}
                        </div>
                      )}
                      {canEdit && revising !== fc.id && (
                        <button className="btn btn-sm" style={{ background: '#1565c0', color: '#fff', border: 'none' }}
                          onClick={() => { setRevising(fc.id); setReviseEdits({}); }}>
                          ✏️ {he ? 'ערוך ושלח מחדש' : 'Revise & Resend'}
                        </button>
                      )}
                      {revising === fc.id && (
                        <>
                          <button className="btn btn-outline btn-sm" onClick={() => { setRevising(null); setReviseEdits({}); }}>
                            {he ? 'ביטול' : 'Cancel'}
                          </button>
                          <button className="btn btn-sm" style={{ background: '#16a34a', color: '#fff', border: 'none' }}
                            onClick={async () => {
                              const items = fc._items.map(item => ({
                                itemId: item.id,
                                qty: reviseEdits[item.id] !== undefined ? reviseEdits[item.id] : (item.customerQty != null ? item.customerQty : item.originalQty),
                              }));
                              try {
                                await api.put(`/portal/fc-revise/${fc.id}`, { items });
                                setRevising(null);
                                setReviseEdits({});
                                setFcOrders(prev => prev.map(f => f.id === fc.id ? { ...f, _items: null } : f));
                                await fetchData();
                              } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                            }}>
                            📤 {he ? 'שלח מחדש ללקוח' : 'Resend to Customer'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
